import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { createAsset, listIntuneOrphans, patchIntuneOrphan } from '@/api/assets';
import { getLocations } from '@/api/locations';
import { listFieldDefinitions } from '@/api/assetFieldDefinitions';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { AssetAttributeFields } from '@/components/AssetAttributeFields';
import { buildAttributes, missingRequired } from '@/lib/assetAttributes';
import { ApiError } from '@/api/client';
import { formatDate, formatDateTime } from '@/lib/dateFormat';
import { assetCategories, lifecycleStates } from './schema';
import { daysSince, deviceTitle, isStaleCheckIn, makeModel, searchText } from './orphanDetail';
import type { AssetCategory, IntuneOrphan, LifecycleState } from '@/types/oscar';

// OS → a best-guess category for the create dialog (the helpdesk can change it).
function suggestCategory(os: string | null): AssetCategory {
  const o = (os ?? '').toLowerCase();
  if (o.includes('windows') || o.includes('mac')) return 'Laptop';
  if (o.includes('ios') || o.includes('ipad') || o.includes('android')) return 'Mobile';
  return 'Other';
}

const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-normal';

const TRIAGE_STATUSES: IntuneOrphan['TriageStatus'][] = ['New', 'Investigating', 'Resolved', 'Ignored'];

const statusBadge: Record<IntuneOrphan['TriageStatus'], string> = {
  New: 'bg-orange/10 text-orange',
  Investigating: 'bg-lightBlue/10 text-lightBlue',
  Resolved: 'bg-green/10 text-green',
  Ignored: 'bg-darkGrey/10 text-darkGrey',
};

/**
 * Intune orphans (PR-09 follow-up) — devices Intune manages that have no
 * matching Oscar asset (no serial match). The Intune sync upserts these into
 * oscar.IntuneOrphan as a Helpdesk triage queue; this page surfaces them
 * (the dashboard widget links here) and lets admin/helpdesk set a triage
 * status. Importing an orphan is deliberately manual — create the asset with
 * the matching serial and the next sync reconciles it.
 */
export function IntuneOrphans() {
  const { canMutate } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Seed the status filter from ?status= so the dashboard "needs triage" tile
  // deep-links straight to the New queue.
  const initialStatus = (() => {
    const s = searchParams.get('status');
    return s && (TRIAGE_STATUSES as string[]).includes(s) ? s : '';
  })();
  const [status, setStatus] = useState(initialStatus);
  const [search, setSearch] = useState('');
  const [triageFor, setTriageFor] = useState<IntuneOrphan | null>(null);
  const [createFor, setCreateFor] = useState<IntuneOrphan | null>(null);

  const query = useQuery({
    queryKey: ['intune-orphans'],
    queryFn: ({ signal }) => listIntuneOrphans(signal),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['intune-orphans'] });
    queryClient.invalidateQueries({ queryKey: ['dash', 'orphans'] });
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  };

  const term = search.trim().toLowerCase();
  const rows = (query.data ?? []).filter((o) =>
    (!status || o.TriageStatus === status) &&
    (!term || searchText(o).some((v) => v.includes(term))),
  );

  return (
    <section>
      <header className="mb-5">
        <Link to="/assets" className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-darkGrey hover:text-navy">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Assets
        </Link>
        <h1 className="font-heading text-2xl font-bold text-navy">Intune orphans</h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          Devices Intune manages that don&rsquo;t match an Oscar asset by serial number.
          To bring one in, create an asset with the matching serial — the next sync reconciles it.
        </p>
      </header>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Triage status
          <select aria-label="Filter by triage status" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
            <option value="">All</option>
            {TRIAGE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search
          <input
            aria-label="Search orphans"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hostname, serial, model, IMEI, user or number"
            className={inputCls}
          />
        </label>
      </div>

      <div className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr>
              <th className="px-3 py-2">Device</th>
              <th className="px-3 py-2">Assigned to</th>
              <th className="px-3 py-2">OS</th>
              <th className="px-3 py-2">Last check-in</th>
              <th className="px-3 py-2">In the queue</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {query.isLoading && <tr><td colSpan={7} className="px-3 py-6 text-center text-darkGrey/60">Loading…</td></tr>}
            {query.isError && <tr><td colSpan={7} className="px-3 py-6 text-center text-red">Could not load orphans.</td></tr>}
            {!query.isLoading && rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-darkGrey/60">No Intune orphans.</td></tr>}
            {rows.map((o) => (
              <tr key={o.IntuneOrphanId} className="border-t border-border hover:bg-bg/60">
                <td className="px-3 py-2 align-top">
                  <DeviceCell o={o} />
                </td>
                <td className="px-3 py-2 align-top text-darkGrey/80">
                  <AssignedCell o={o} />
                </td>
                <td className="px-3 py-2 align-top text-darkGrey/80">
                  <OsCell o={o} />
                </td>
                <td className="px-3 py-2 align-top text-darkGrey/80">
                  <CheckInCell o={o} />
                </td>
                <td className="px-3 py-2 align-top text-xs text-darkGrey/80">
                  <div>First seen {formatDateTime(o.FirstSeenDate)}</div>
                  <div>Last seen {formatDateTime(o.LastSeenDate)}</div>
                </td>
                <td className="px-3 py-2 align-top">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadge[o.TriageStatus]}`}>{o.TriageStatus}</span>
                </td>
                <td className="px-3 py-2 text-right align-top">
                  {canMutate && (
                    <div className="flex items-center justify-end gap-3">
                      {o.SerialNumber && o.TriageStatus !== 'Resolved' && (
                        <button type="button" onClick={() => setCreateFor(o)} className="text-xs font-semibold text-orange hover:underline">Create asset</button>
                      )}
                      <button type="button" onClick={() => setTriageFor(o)} className="text-xs font-semibold text-navy hover:underline">Triage…</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {triageFor && <TriageDialog orphan={triageFor} onClose={() => setTriageFor(null)} onSaved={() => { invalidate(); setTriageFor(null); }} />}
      {createFor && <CreateAssetDialog orphan={createFor} onClose={() => setCreateFor(null)} onSaved={() => { invalidate(); setCreateFor(null); }} />}
    </section>
  );
}

/**
 * ⛔ THE DEVICE, NOT ITS ID (Anthony, 01/11/2026: "currently it's only showing an ID"). Intune sends the
 * hostname, make, model, IMEI and number for every device, and the sync has always stored them; the list
 * simply never showed them. A device with no serial used to be a row of dashes.
 */
function DeviceCell({ o }: { o: IntuneOrphan }) {
  const mm = makeModel(o);
  return (
    <div className="space-y-0.5">
      <div className="font-semibold text-navy">{deviceTitle(o)}</div>
      {mm && o.Hostname && <div className="text-xs text-darkGrey/80">{mm}</div>}
      {o.SerialNumber
        ? <div className="font-mono text-xs text-darkGrey/80">{o.SerialNumber}</div>
        : (
          <div className="text-xs font-semibold text-orange" title="Intune reports no serial number for this device, so Oscar cannot match it to an asset by serial.">
            No serial number
          </div>
        )}
      {o.Imei && <div className="text-xs text-darkGrey/70">IMEI <span className="font-mono">{o.Imei}</span></div>}
      {o.PhoneNumber && <div className="text-xs text-darkGrey/70">{o.PhoneNumber}</div>}
    </div>
  );
}

/** The Oscar person when the address is theirs, with the address under it; the address alone when it is not. */
function AssignedCell({ o }: { o: IntuneOrphan }) {
  const lastLogon = o.LastLogonUpn && o.LastLogonUpn.toLowerCase() !== (o.AssignedUpn ?? '').toLowerCase() ? o.LastLogonUpn : null;
  return (
    <div className="space-y-0.5">
      {o.AssignedPersonName && <div className="font-medium text-navy">{o.AssignedPersonName}</div>}
      {o.AssignedUpn
        ? <div className={o.AssignedPersonName ? 'text-xs' : ''}>{o.AssignedUpn}</div>
        : <div className="text-xs text-darkGrey/60">Nobody assigned</div>}
      {lastLogon && <div className="text-xs text-darkGrey/70">Last logon {lastLogon}</div>}
    </div>
  );
}

function OsCell({ o }: { o: IntuneOrphan }) {
  return (
    <div className="space-y-0.5">
      <div>{o.OsType ?? '—'}</div>
      {o.OsVersion && <div className="text-xs text-darkGrey/70">{o.OsVersion}</div>}
      {o.Compliance && <div className="text-xs text-darkGrey/70">{o.Compliance}</div>}
      {o.Ownership && <div className="text-xs text-darkGrey/70">{o.Ownership}</div>}
    </div>
  );
}

/**
 * When the device last spoke to Intune, and a flag when that is a month ago or more: the first thing
 * to know about an orphan is whether it is still alive.
 *
 * ⚠ A dash means "not known yet" (the sync after the migration fills it), never "never checked in".
 */
function CheckInCell({ o }: { o: IntuneOrphan }) {
  if (!o.LastCheckIn) return <span className="text-darkGrey/50" title="Not known yet. It is filled in by the next Intune sync.">—</span>;
  const days = daysSince(o.LastCheckIn);
  return (
    <div className="space-y-0.5">
      <div>{formatDate(o.LastCheckIn)}</div>
      {isStaleCheckIn(o.LastCheckIn)
        ? <div className="text-xs font-semibold text-orange">{days} days ago — probably gone</div>
        : <div className="text-xs text-darkGrey/70">{days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} ago`}</div>}
    </div>
  );
}

/**
 * Create an Oscar asset from an Intune orphan. Intune supplies everything it
 * knows — serial, make, model, hostname and number are pre-filled from the
 * orphan record (migration 030) and remain editable — so the helpdesk only has
 * to choose Status and Location (meeting 29/06 item 10). On success the orphan
 * is marked Resolved; the next Intune sync reconciles by serial and fills the
 * Intune* columns (including the primary user).
 */
function CreateAssetDialog({ orphan, onClose, onSaved }: { orphan: IntuneOrphan; onClose: () => void; onSaved: () => void }) {
  const [category, setCategory] = useState<AssetCategory>(suggestCategory(orphan.OsType));
  const [lifecycle, setLifecycle] = useState<LifecycleState>('Deployed');
  // Pre-filled from Intune, editable.
  const [make, setMake] = useState(orphan.Manufacturer ?? '');
  const [model, setModel] = useState(orphan.Model ?? '');
  const [hostname, setHostname] = useState(orphan.Hostname ?? '');
  const [msisdn, setMsisdn] = useState(orphan.PhoneNumber ?? '');
  const [locationId, setLocationId] = useState<string>('');
  const [notes, setNotes] = useState(`Created from Intune orphan${orphan.AssignedUpn ? ` — assigned to ${orphan.AssignedUpn}` : ''}.`);
  const [attrs, setAttrs] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const locationsQuery = useQuery({ queryKey: ['locations', 'active'], queryFn: ({ signal }) => getLocations({ activeOnly: true }, signal), retry: false });
  // Category-specific custom fields (oscar.AssetFieldDefinition) — so required fields can be satisfied on import.
  const defsQuery = useQuery({ queryKey: ['asset-field-definitions', category], queryFn: ({ signal }) => listFieldDefinitions(category, signal), enabled: !!category });
  const defs = useMemo(() => defsQuery.data ?? [], [defsQuery.data]);

  // Pre-fill an IMEI custom field from the orphan's Intune IMEI (migration 032).
  useEffect(() => {
    const imeiDef = defs.find((d) => d.FieldKey.toLowerCase() === 'imei');
    if (imeiDef && orphan.Imei) setAttrs((s) => (s[imeiDef.FieldKey] ? s : { ...s, [imeiDef.FieldKey]: orphan.Imei! }));
  }, [defs, orphan.Imei]);

  const missing = missingRequired(defs, attrs);

  const mutation = useMutation({
    mutationFn: async () => {
      const attributes = buildAttributes(defs, attrs);
      const asset = await createAsset({
        SerialNumber: orphan.SerialNumber,
        Category: category,
        LifecycleState: lifecycle,
        Make: make.trim() || undefined,
        Model: model.trim() || undefined,
        Hostname: hostname.trim() || undefined,
        Msisdn: msisdn.trim() || undefined,
        LocationId: locationId ? Number(locationId) : undefined,
        Attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
        Notes: notes.trim() || undefined,
      });
      await patchIntuneOrphan(orphan.IntuneOrphanId, { TriageStatus: 'Resolved', Notes: `Imported as asset #${asset.AssetId}.` });
      return asset;
    },
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not create the asset.'),
  });

  return (
    <AlertDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Create asset — ${orphan.SerialNumber}`}
      confirmLabel={mutation.isPending ? 'Creating…' : 'Create asset'}
      closeOnConfirm={false}
      onConfirm={() => {
        if (missing.length > 0) { setError(`Complete the required field${missing.length > 1 ? 's' : ''}: ${missing.map((d) => d.Label).join(', ')}.`); return; }
        setError(null);
        mutation.mutate();
      }}
    >
      <div className="space-y-2">
        {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{error}</div>}
        <p className="text-xs text-darkGrey/70">
          Serial <span className="font-mono text-navy">{orphan.SerialNumber}</span>
          {orphan.OsType ? <> · {orphan.OsType}</> : null}
          {orphan.AssignedUpn ? <> · {orphan.AssignedUpn}</> : null}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs font-semibold text-darkGrey">Category
            <select value={category} onChange={(e) => setCategory(e.target.value as AssetCategory)} className={inputCls}>
              {assetCategories.filter((c) => c !== 'SIM').map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block text-xs font-semibold text-darkGrey">Status
            <select value={lifecycle} onChange={(e) => setLifecycle(e.target.value as LifecycleState)} className={inputCls}>
              {lifecycleStates.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block text-xs font-semibold text-darkGrey">Make
            <input value={make} onChange={(e) => setMake(e.target.value)} className={inputCls} />
          </label>
          <label className="block text-xs font-semibold text-darkGrey">Model
            <input value={model} onChange={(e) => setModel(e.target.value)} className={inputCls} />
          </label>
          <label className="block text-xs font-semibold text-darkGrey">Hostname
            <input value={hostname} onChange={(e) => setHostname(e.target.value)} className={inputCls} />
          </label>
          <label className="block text-xs font-semibold text-darkGrey">Number
            <input value={msisdn} onChange={(e) => setMsisdn(e.target.value)} className={inputCls} />
          </label>
        </div>
        <label className="block text-xs font-semibold text-darkGrey">Location
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className={inputCls}>
            <option value="">— Not set —</option>
            {(locationsQuery.data ?? []).map((l) => <option key={l.LocationId} value={l.LocationId}>{l.LocationDesc}</option>)}
          </select>
        </label>
        {defs.length > 0 && (
          <fieldset className="rounded border border-border bg-bg/40 p-2">
            <legend className="px-1 text-xs font-semibold text-darkGrey">{category} fields</legend>
            <AssetAttributeFields defs={defs} values={attrs} onChange={setAttrs} />
          </fieldset>
        )}
        <label className="block text-xs font-semibold text-darkGrey">Notes
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls} />
        </label>
        <p className="text-xs text-darkGrey/60">Make, model, hostname and number are pre-filled from Intune — adjust if needed. Any required {category.toLowerCase()} fields must be completed. The next Intune sync will reconcile by serial and fill the remaining Intune details (including the primary user).</p>
      </div>
    </AlertDialog>
  );
}

function TriageDialog({ orphan, onClose, onSaved }: { orphan: IntuneOrphan; onClose: () => void; onSaved: () => void }) {
  const [triageStatus, setTriageStatus] = useState<IntuneOrphan['TriageStatus']>(orphan.TriageStatus);
  const [notes, setNotes] = useState(orphan.Notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => patchIntuneOrphan(orphan.IntuneOrphanId, { TriageStatus: triageStatus, Notes: notes.trim() || undefined }),
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not update the orphan.'),
  });

  return (
    <AlertDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Triage — ${deviceTitle(orphan)}`}
      confirmLabel="Save"
      onConfirm={() => { setError(null); mutation.mutate(); }}
    >
      <div className="space-y-2">
        {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{error}</div>}
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 rounded border border-border bg-bg/40 p-2 text-xs text-darkGrey">
          <Fact label="Device" value={deviceTitle(orphan)} />
          <Fact label="Make and model" value={makeModel(orphan)} />
          <Fact label="Serial number" value={orphan.SerialNumber} mono />
          <Fact label="IMEI" value={orphan.Imei} mono />
          <Fact label="Number" value={orphan.PhoneNumber} />
          <Fact label="Assigned to" value={[orphan.AssignedPersonName, orphan.AssignedUpn].filter(Boolean).join(' · ') || null} />
          <Fact label="Last logon" value={orphan.LastLogonUpn ? `${orphan.LastLogonUpn}${orphan.LastLogonDate ? ` (${formatDate(orphan.LastLogonDate)})` : ''}` : null} />
          <Fact label="OS" value={[orphan.OsType, orphan.OsVersion].filter(Boolean).join(' ') || null} />
          <Fact label="Compliance" value={orphan.Compliance} />
          <Fact label="Ownership" value={orphan.Ownership} />
          <Fact label="Enrolled" value={orphan.EnrolledDate ? formatDate(orphan.EnrolledDate) : null} />
          <Fact label="Last check-in" value={orphan.LastCheckIn ? formatDate(orphan.LastCheckIn) : null} />
          <Fact label="Intune device ID" value={orphan.IntuneDeviceId} mono />
        </dl>
        <label className="block text-xs font-semibold text-darkGrey">Triage status
          <select value={triageStatus} onChange={(e) => setTriageStatus(e.target.value as IntuneOrphan['TriageStatus'])} className={inputCls}>
            {TRIAGE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold text-darkGrey">Notes
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={inputCls} />
        </label>
      </div>
    </AlertDialog>
  );
}

/** One line of the triage dialog's device detail; a dash when Intune did not say (or has not been asked since the migration). */
function Fact({ label, value, mono = false }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <>
      <dt className="font-semibold text-darkGrey/70">{label}</dt>
      <dd className={mono ? 'break-all font-mono' : 'break-words'}>{value || '—'}</dd>
    </>
  );
}
